// OpenAPI integration templates — Step 0 picker tiles.
//
// Each template carries:
//   - name / slug / icon / description (UI fluff)
//   - base_url (pre-fills Step 3, can be re-templated for {your-org})
//   - auth_method + auth_config (pre-fills auth dropdown + sub-field)
//   - credential_field + credential_label (titles the inline secret input)
//   - spec_url      — preferred: a published OpenAPI 3.x JSON/YAML URL
//                     (Path A in obtain_spec — no LLM call, full surface).
//   - docs_url      — fallback: an HTML docs page URL (Path C in
//                     obtain_spec — LLM authors a spec from prose).
//                     Used for APIs without a public OpenAPI spec
//                     (Alpha Vantage, OpenWeather, etc.).
//
// The wizard's tile-click handler runs obtain_spec with the right URL
// (spec_url first, docs_url as fallback). Result: full surface
// auto-loaded into Step 2 — not a 1-op demo. Bundled inline specs
// (which the v1 templates carried) are gone — they were always
// demo-grade and the user couldn't tell whether they'd just registered
// the actual API or a thin wrapper around one operation.
//
// Picking a template never blocks any other path: "Start from scratch"
// is always there for APIs not in this list, and within the wizard
// the "Strict OpenAPI URL" / "Paste JSON / YAML" modes still work.

export const OPENAPI_TEMPLATES = [
  // ───────────────────────────── Alpha Vantage ────────────────────
  {
    id: "alphavantage",
    name: "Alpha Vantage",
    description: "Real-time stock quotes, time-series, fundamentals, news, forex, crypto.",
    icon: "📈",
    base_url: "https://www.alphavantage.co",
    base_url_editable: false,
    auth_method: "api_key_query",
    auth_config: { query_name: "apikey" },
    credential_field: "api_key",
    credential_label: "API key",
    credential_help_url: "https://www.alphavantage.co/support/#api-key",
    // No public OpenAPI spec — use the docs page (Path C).
    docs_url: "https://www.alphavantage.co/documentation/",
    docs_hint: "Cover the most useful equity / news functions: GLOBAL_QUOTE, TIME_SERIES_INTRADAY, TIME_SERIES_DAILY, OVERVIEW, INCOME_STATEMENT, BALANCE_SHEET, EARNINGS, SYMBOL_SEARCH, NEWS_SENTIMENT, MARKET_STATUS. Declare `function` as an enum and tag each parameter with which functions need it.",
  },

  // ───────────────────────────── OpenAI ──────────────────────────
  {
    id: "openai",
    name: "OpenAI",
    description: "Models, files, assistants, fine-tuning. Full official spec.",
    icon: "🧠",
    base_url: "https://api.openai.com/v1",
    base_url_editable: false,
    auth_method: "bearer",
    auth_config: {},
    credential_field: "token",
    credential_label: "API key (sk-…)",
    credential_help_url: "https://platform.openai.com/api-keys",
    // OpenAI uses date-tagged branches (no master openapi.yaml). Pin
    // to a known-good branch; bump periodically. URL audit cron is
    // tracked as a follow-up.
    spec_url: "https://raw.githubusercontent.com/openai/openai-openapi/2025-03-21/openapi.yaml",
    // Fallback: if the published spec fails the platform validator
    // (deep `$ref` composition is the common case), the wizard drops
    // to docs-prose authoring against the public API reference.
    docs_url: "https://platform.openai.com/docs/api-reference",
    docs_hint: "Cover the most agent-useful operations: list models, create chat completion (non-streaming), retrieve assistant, list files, retrieve file. Skip streaming, beta endpoints, and audio/embeddings.",
  },

  // ───────────────────────────── Stripe ──────────────────────────
  {
    id: "stripe",
    name: "Stripe",
    description: "Customers, charges, subscriptions, invoices. Full official spec.",
    icon: "💳",
    base_url: "https://api.stripe.com/v1",
    base_url_editable: false,
    auth_method: "bearer",
    auth_config: {},
    credential_field: "token",
    credential_label: "Secret key (sk_…)",
    credential_help_url: "https://dashboard.stripe.com/apikeys",
    spec_url: "https://raw.githubusercontent.com/stripe/openapi/master/openapi/spec3.json",
    docs_url: "https://docs.stripe.com/api",
    docs_hint: "Cover the most-used resources: customers (list/get/create), charges (list/get), payment_intents (create/get), subscriptions (list/get), invoices (list/get). Skip Connect-only endpoints unless the user asks.",
  },

  // ───────────────────────────── Twilio ──────────────────────────
  {
    id: "twilio",
    name: "Twilio",
    description: "SMS, voice, accounts, messages. HTTP Basic auth (account_sid:auth_token).",
    icon: "📞",
    base_url: "https://api.twilio.com",
    base_url_editable: false,
    auth_method: "basic",
    auth_config: {},
    credential_field: "basic_auth",
    credential_label: "account_sid:auth_token",
    credential_label_helper: "Format: ACxxxxx:your_auth_token",
    credential_help_url: "https://console.twilio.com/",
    // Twilio publishes per-product specs; the Core API spec covers
    // accounts/messages/calls.
    spec_url: "https://raw.githubusercontent.com/twilio/twilio-oai/main/spec/json/twilio_api_v2010.json",
    docs_url: "https://www.twilio.com/docs/usage/api",
  },

  // ───────────────────────────── Slack ───────────────────────────
  {
    id: "slack",
    name: "Slack",
    description: "Messages, channels, users, files. Bot OAuth token (xoxb-…).",
    icon: "💬",
    base_url: "https://slack.com/api",
    base_url_editable: false,
    auth_method: "bearer",
    auth_config: {},
    credential_field: "token",
    credential_label: "Bot user OAuth token (xoxb-…)",
    credential_help_url: "https://api.slack.com/apps",
    // Slack's Web API is documented but specs are per-method; their
    // openapi/web-api.json on GitHub is the closest thing to a master.
    spec_url: "https://raw.githubusercontent.com/slackapi/slack-api-specs/master/web-api/slack_web_openapi_v2.json",
    docs_url: "https://api.slack.com/web",
  },

  // ───────────────────────────── Jira Cloud ──────────────────────
  {
    id: "jira",
    name: "Jira Cloud",
    description: "Issues, projects, users on Atlassian Cloud Jira.",
    icon: "🗂️",
    base_url: "https://your-org.atlassian.net",
    base_url_editable: true,
    base_url_helper: "Replace `your-org` with your Atlassian site name.",
    auth_method: "basic",
    auth_config: {},
    credential_field: "basic_auth",
    credential_label: "email:api_token",
    credential_label_helper: "Format: you@example.com:atlassian_api_token",
    credential_help_url: "https://id.atlassian.com/manage-profile/security/api-tokens",
    spec_url: "https://developer.atlassian.com/cloud/jira/platform/swagger-v3.v3.json",
    docs_url: "https://developer.atlassian.com/cloud/jira/platform/rest/v3/intro/",
  },

  // ───────────────────────────── GitHub ──────────────────────────
  {
    id: "github",
    name: "GitHub",
    description: "Repos, issues, pull requests, actions. Full REST API.",
    icon: "🐙",
    base_url: "https://api.github.com",
    base_url_editable: false,
    auth_method: "bearer",
    auth_config: {},
    credential_field: "token",
    credential_label: "Personal access token (ghp_… / github_pat_…)",
    credential_help_url: "https://github.com/settings/tokens",
    spec_url: "https://raw.githubusercontent.com/github/rest-api-description/main/descriptions/api.github.com/api.github.com.json",
    docs_url: "https://docs.github.com/en/rest",
    docs_hint: "Cover the agent-relevant subset: get authenticated user, list user repos, get repo, list/get/create issues, list/get pull requests, list commits. Skip GraphQL, Actions internals, and admin-only endpoints.",
  },

  // ───────────────────────────── OpenWeather ─────────────────────
  {
    id: "openweather",
    name: "OpenWeatherMap",
    description: "Current weather, forecast, history by city or coordinates.",
    icon: "☀️",
    base_url: "https://api.openweathermap.org",
    base_url_editable: false,
    auth_method: "api_key_query",
    auth_config: { query_name: "appid" },
    credential_field: "api_key",
    credential_label: "API key",
    credential_help_url: "https://home.openweathermap.org/api_keys",
    docs_url: "https://openweathermap.org/api",
    docs_hint: "Cover Current Weather (/data/2.5/weather), 5-day forecast (/data/2.5/forecast), and the One Call API. `q` (city name) or `lat`+`lon` are the typical input combos; `units` is enum [metric, imperial, standard].",
  },

  // ───────────────────────────── ServiceNow ──────────────────────
  {
    id: "servicenow",
    name: "ServiceNow",
    description: "Incidents, requests, change management. BYO Swagger spec — instance-specific.",
    icon: "🛠️",
    base_url: "https://your-instance.service-now.com",
    base_url_editable: true,
    base_url_helper: "Replace `your-instance` with your ServiceNow instance subdomain.",
    auth_method: "basic",
    auth_config: {},
    credential_field: "basic_auth",
    credential_label: "username:password",
    credential_label_helper: "Service-account credentials are recommended.",
    credential_help_url: "https://docs.servicenow.com/",
    // ServiceNow's docs are a JS-rendered SPA (server returns ~140
    // chars of "Loading application..." text, not real prose). Each
    // org also has a unique instance with its own Swagger endpoint
    // at `/api/now/swagger.json` — so the right shape is "BYO spec"
    // with pre-filled auth + base_url. Tile click lands on Step 1
    // (spec source) instead of attempting a doomed Smart-fetch.
    byo_spec: true,
    byo_spec_helper: "Open your instance Swagger at https://<your-instance>.service-now.com/api/now/swagger.json — paste it into Step 1.",
  },

  // ───────────────────────────── Salesforce ──────────────────────
  {
    id: "salesforce",
    name: "Salesforce",
    description: "REST API on a Salesforce org. BYO spec — Connected App / OAuth2.",
    icon: "☁️",
    base_url: "https://your-org.my.salesforce.com",
    base_url_editable: true,
    base_url_helper: "Use your `My Domain` URL from Salesforce Setup.",
    auth_method: "oauth2_client_credentials",
    auth_config: {},
    credential_field: "client_secret",
    credential_label: "Connected App consumer key:consumer secret",
    credential_label_helper: "Format: 3MVG9…:abc123…  (key:secret colon-joined)",
    credential_help_url: "https://help.salesforce.com/s/articleView?id=xcloud.connected_app_overview.htm",
    // Salesforce REST API: every org has a unique instance, no
    // master spec URL. Force-paste your generated OpenAPI export.
    byo_spec: true,
    byo_spec_helper: "Generate your org's OpenAPI from Setup → External Services → API Specifications, or use a community generator like sf-openapi.",
  },
];


// Helper for UI: derive the credential_ref the user shouldn't have to
// type. Format: openapi.<slug>.<credential_field>. Both segments must
// satisfy tenant_settings_repo._OPENAPI_CRED_KEY_RE — lowercase
// alphanumeric + underscore.
export function deriveCredentialRef(slug, credentialField) {
  if (!slug || !credentialField) return "";
  const safe = (s) => String(s).toLowerCase().replace(/[^a-z0-9_]/g, "_");
  return `openapi.${safe(slug)}.${safe(credentialField)}`;
}


// Pick the best obtain-spec source URL for a template.
// Preference: spec_url (Path A — deterministic, no LLM) > docs_url (Path C).
// Returns null for byo_spec templates and templates with no source URL —
// caller should route these to Step 1 (spec source) instead of Smart-fetch.
export function templateFetchTarget(t) {
  if (!t) return null;
  if (t.byo_spec) return null;     // user supplies the spec themselves
  if (t.spec_url) return { url: t.spec_url, kind: "spec" };
  if (t.docs_url) return { url: t.docs_url, kind: "docs", hint: t.docs_hint || null };
  return null;
}
